/* eslint-disable */

// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import InactivityWarning from '../components/InactivityWarning';
import BreakOverlay from '../components/BreakOverlay';
import toast from 'react-hot-toast';

const AuthContext = createContext();

const INACTIVITY_MS      = 10 * 60 * 1000; // 10 minutes total
const WARNING_BEFORE_MS  =  2 * 60 * 1000; // warn 2 minutes before logout
const HEARTBEAT_MS       = 45 * 1000;      // heartbeat every 45 seconds for accurate real-time presence

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('crm_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [loading,         setLoading]         = useState(true);
  const [showWarning,     setShowWarning]      = useState(false);
  const [warningSeconds,  setWarningSeconds]   = useState(120);
  const [userStatus,      setUserStatus]       = useState('online');
  const [breakSecondsLeft, setBreakSecondsLeft] = useState(3600); // 1 hour for BreakOverlay

  // Refs (don't cause re-renders)
  const inactivityTimer = useRef(null);
  const warningTimer    = useRef(null);
  const countdownRef    = useRef(null);
  const heartbeatRef    = useRef(null);
  const userRef         = useRef(user);
  const statusRef       = useRef(userStatus);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { 
    statusRef.current = userStatus; 
    if (userRef.current) {
      api.post('/attendance/heartbeat', { status: userStatus }).catch(() => {});
    }
  }, [userStatus]);

  // ── Attendance helpers ────────────────────────────────────
  const checkIn = useCallback(async () => {
    try { await api.post('/attendance/checkin'); } catch { /* silent */ }
  }, []);

  const checkOut = useCallback(async (reason = 'manual') => {
    try { await api.post('/attendance/checkout', { reason }); } catch { /* silent */ }
  }, []);

  const sendHeartbeat = useCallback(async () => {
    try {
      const { data } = await api.post('/attendance/heartbeat', { status: statusRef.current });
      
      if (statusRef.current === 'break' || statusRef.current === 'Break') {
        const mins = data?.breakMins || 0;
        const left = (60 - mins) * 60;
        setBreakSecondsLeft(left > 0 ? left : 0);
      }

      if (data?.breakMins >= 60) {
        toast.error("You have exceeded the maximum 1 hour break limit and have been logged out.", { duration: 5000 });
        logout('auto');
      }
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Logout ───────────────────────────────────────────────
  const logout = useCallback(async (reason = 'manual') => {
    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownRef.current);
    clearInterval(heartbeatRef.current);
    setShowWarning(false);

    if (userRef.current) {
      await checkOut(reason);
      // Log activity
      try { await api.post('/activity', { action: reason === 'auto' ? 'AUTO_LOGOUT' : 'LOGOUT' }); } catch {}
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('crm_user');
    setUser(null);
  }, [checkOut]);

  // ── Inactivity timer logic ────────────────────────────────
  const startWarningCountdown = useCallback(() => {
    setWarningSeconds(120);
    setShowWarning(true);

    // Tick countdown every second
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setWarningSeconds(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setUserStatus('break');
          setShowWarning(false);
          toast("You have been marked on Break due to inactivity.");
          return 0;
        }
        return prev - 1;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps

    }, 1000);
  }, [logout]);

  const resetInactivityTimer = useCallback(() => {
    if (!userRef.current) return;
    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);

    // If warning was showing, dismiss it
    if (showWarning) {
      setShowWarning(false);
      clearInterval(countdownRef.current);
    }

    // Set warning timer (fires at INACTIVITY_MS - WARNING_BEFORE_MS)
    warningTimer.current = setTimeout(() => {
      startWarningCountdown();
    }, INACTIVITY_MS - WARNING_BEFORE_MS);

  }, [showWarning, startWarningCountdown]);

  // ── "Continue Working" handler ────────────────────────────
  const handleContinue = useCallback(() => {
    setShowWarning(false);
    clearInterval(countdownRef.current);
    resetInactivityTimer();
    // Log that warning was dismissed
    try { api.post('/activity', { action: 'INACTIVITY_WARNING_DISMISSED' }).catch(() => {}); } catch {}
  }, [resetInactivityTimer]);

  // ── Verify token on mount ─────────────────────────────────
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await api.get('/me');
        setUser(data);
        localStorage.setItem('crm_user', JSON.stringify(data));
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('crm_user');
        setUser(null);
      } finally { setLoading(false); }
    };
    verifyToken();
  }, []);

  // ── Login ─────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', data.token);
    localStorage.setItem('crm_user', JSON.stringify(data.user));
    setUser(data.user);

    // Auto check-in on login
    setTimeout(async () => {
      try { await api.post('/attendance/checkin'); } catch {}
      try { await api.post('/activity', { action: 'LOGIN', metadata: { email } }); } catch {}
    }, 500);

    return data.user;
  }, []);

  // ── Inactivity tracking when user is logged in ────────────
  useEffect(() => {
    if (!user) return;

    // Start heartbeat immediately on mount and on recurring interval
    sendHeartbeat();
    clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_MS);

    // Activity & presence event listeners
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const onActivity = () => {
      resetInactivityTimer();
    };
    if (user.role !== 'Admin') {
      events.forEach(ev => window.addEventListener(ev, onActivity));
      resetInactivityTimer(); // Initial timer start
    }

    // When tab becomes active/visible again, immediately refresh presence heartbeat
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
        resetInactivityTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);

    const inactTimer = inactivityTimer.current;
    const warnTimer = warningTimer.current;
    const countTimer = countdownRef.current;
    const heartTimer = heartbeatRef.current;

    // Send instant keepalive checkout ONLY when actual window/tab unloads (not just tab switch)
    const handlePageHide = (e) => {
      if (e && e.persisted) return; // Tab cached in back-forward cache, not closed
      if (userRef.current && userRef.current.role !== 'Admin') {
        const token = localStorage.getItem('accessToken');
        if (token) {
          try {
            fetch('/api/attendance/checkout', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ reason: 'auto' }),
              keepalive: true
            });
          } catch (_) {}
        }
      }
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      events.forEach(ev => window.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
      window.removeEventListener('pagehide', handlePageHide);
      clearTimeout(inactTimer);
      clearTimeout(warnTimer);
      clearInterval(countTimer);
      clearInterval(heartTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Break timer ticking ───────────────────────────────────
  useEffect(() => {
    let tickInterval = null;
    if (userStatus === 'break') {
      tickInterval = setInterval(() => {
        setBreakSecondsLeft(prev => {
          if (prev <= 1) {
             clearInterval(tickInterval);
             toast.error("You have exceeded the maximum 1 hour break limit and have been logged out.", { duration: 5000 });
             logout('auto');
             return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(tickInterval);
  }, [userStatus, logout]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, checkIn, checkOut, userStatus, setUserStatus }}>
      {children}
      {/* Inactivity Warning Modal — rendered at top level so it overlays everything */}
      <InactivityWarning
        show={showWarning && userStatus !== 'break'}
        secondsLeft={warningSeconds}
        onContinue={handleContinue}
        onLogout={() => logout('manual')}
      />
      <BreakOverlay
        show={userStatus === 'break'}
        secondsLeft={breakSecondsLeft}
        onEndBreak={() => setUserStatus('online')}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);